import { useState, useCallback } from 'react';
import { useDocumentAPI } from '../DocumentAPI';

interface VersionDecision {
  action: 'create_version' | 'overwrite_current' | 'cancel';
  versionDescription?: string;
  versionType: 'major' | 'minor' | 'patch';
  includeDiff: boolean;
  diffSummary?: string;
}

interface UseDocumentVersioningProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function useDocumentVersioning({ onSuccess, onError }: UseDocumentVersioningProps = {}) {
  const documentAPI = useDocumentAPI();
  const [isVersioningModalOpen, setIsVersioningModalOpen] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [currentContent, setCurrentContent] = useState<string>('');
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Open versioning modal
  const openVersioningModal = useCallback((
    documentId: string, 
    content: string, 
    title: string = 'Document'
  ) => {
    setCurrentDocumentId(documentId);
    setCurrentContent(content);
    setDocumentTitle(title);
    setIsVersioningModalOpen(true);
  }, []);

  // Close versioning modal
  const closeVersioningModal = useCallback(() => {
    setIsVersioningModalOpen(false);
    setCurrentDocumentId('');
    setCurrentContent('');
    setDocumentTitle('');
  }, []);

  // Handle version decision
  const handleVersionDecision = useCallback(async (decision: VersionDecision) => {
    if (decision.action === 'cancel') {
      closeVersioningModal();
      return;
    }

    setIsProcessing(true);

    try {
      // Both actions use saveLexicalContent - it automatically handles version creation when content changes
      await documentAPI.saveLexicalContent(
        currentDocumentId, 
        currentContent, 
        decision.versionDescription || 'Content updated'
      );

      closeVersioningModal();
      onSuccess?.();
    } catch (error) {
      console.error('Error processing version decision:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process version decision';
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [currentDocumentId, currentContent, documentAPI, closeVersioningModal, onSuccess, onError]);

  // Check if content has changed (simple comparison)
  const hasContentChanged = useCallback((originalContent: string, newContent: string): boolean => {
    return originalContent.trim() !== newContent.trim();
  }, []);

  // Auto-detect if versioning is needed
  const shouldShowVersioningModal = useCallback((
    originalContent: string, 
    newContent: string, 
    changeThreshold: number = 5
  ): boolean => {
    if (!hasContentChanged(originalContent, newContent)) {
      return false;
    }

    // Calculate change percentage
    const originalLength = originalContent.length;
    const newLength = newContent.length;
    const changePercentage = originalLength > 0 ? 
      Math.abs((newLength - originalLength) / originalLength * 100) : 0;

    return changePercentage >= changeThreshold;
  }, [hasContentChanged]);

  return {
    // State
    isVersioningModalOpen,
    currentDocumentId,
    currentContent,
    documentTitle,
    isProcessing,

    // Actions
    openVersioningModal,
    closeVersioningModal,
    handleVersionDecision,

    // Utilities
    hasContentChanged,
    shouldShowVersioningModal
  };
} 